import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Permission } from '@mt/shared';
import * as client from './api';
import { dropDevicePush } from './pwa';

interface AuthState {
  user: client.SessionUser | null;
  ready: boolean;
  /** Mở ứng dụng khi mất mạng: đang dùng hồ sơ đăng nhập lưu trên máy, chưa có phiên với máy chủ (X-PWA). */
  offline: boolean;
  can: (p: Permission) => boolean;
  login: (u: string, p: string) => Promise<void>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<client.SessionUser | null>(null);
  const [ready, setReady] = useState(false);
  const [offline, setOffline] = useState(false);

  useEffect(() => {
    client.setSessionListener((u) => {
      setUser(u);
      setOffline(false);
      saveSnapshot(u);
    });
    // Khôi phục phiên từ cookie refresh khi mở lại trang. Mất mạng mà máy đã từng đăng nhập: vào bằng hồ sơ lưu trên máy
    // để chấm công / xem màn đã lưu; thao tác ghi tự vào hàng chờ, có mạng thì làm mới phiên thật.
    client
      .refreshSession()
      .then((s) => {
        if (s || !client.lastRefreshWasOffline()) return;
        const snap = loadSnapshot();
        if (snap) {
          setUser(snap);
          setOffline(true);
        }
      })
      .finally(() => setReady(true));
  }, []);

  useEffect(() => {
    if (!offline) return;
    const on = () => void client.refreshSession();
    window.addEventListener('online', on);
    const t = setInterval(on, 30_000);
    return () => {
      window.removeEventListener('online', on);
      clearInterval(t);
    };
  }, [offline]);

  const can = useCallback((p: Permission) => !!user?.permissions.includes(p), [user]);
  const value = useMemo<AuthState>(
    () => ({
      user,
      ready,
      offline,
      can,
      login: async (u, p) => {
        await client.login(u, p);
      },
      logout: async () => {
        await dropDevicePush((endpoint) => client.api.post('/push/subscriptions/remove', { endpoint }));
        await client.logout();
      },
    }),
    [user, ready, offline, can],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth phải nằm trong AuthProvider');
  return v;
}

/**
 * Hồ sơ đăng nhập lưu trên máy (tên, vai trò, quyền; KHÔNG có token) để mở ứng dụng khi mất mạng.
 * Chỉ dùng để vẽ giao diện; máy chủ vẫn kiểm quyền mọi thao tác. Đăng xuất / phiên bị thu hồi thì xóa.
 */
const SNAPSHOT_KEY = 'mt.session.snapshot';
function saveSnapshot(u: client.SessionUser | null) {
  try {
    if (u) localStorage.setItem(SNAPSHOT_KEY, JSON.stringify({ ...u, savedAt: Date.now() }));
    else localStorage.removeItem(SNAPSHOT_KEY);
  } catch {
    /* trình duyệt chặn lưu – bỏ qua */
  }
}
const SNAPSHOT_MAX_AGE_MS = 30 * 24 * 3600_000;
function loadSnapshot(): client.SessionUser | null {
  try {
    const v = JSON.parse(localStorage.getItem(SNAPSHOT_KEY) ?? 'null') as (client.SessionUser & { savedAt?: number }) | null;
    if (!v || typeof v.id !== 'number' || !Array.isArray(v.permissions)) return null;
    if (!v.savedAt || Date.now() - v.savedAt > SNAPSHOT_MAX_AGE_MS) return null;
    const { savedAt: _s, ...user } = v;
    return { ...user, mustChangePassword: false };
  } catch {
    return null;
  }
}
